import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useActivitiesStore } from '../../store/activitiesStore';
import { useTranslation } from 'react-i18next';
import { CollapsibleNav } from '../navigation/CollapsibleNav';

interface SidebarProps {
  width: number;
  onWidthChange: (width: number) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ width, onWidthChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activities, setCurrentActivityId, removeActivity, clearAllActivities } = useActivitiesStore();
  const [isResizing, setIsResizing] = React.useState(false);
  const [isResizingDivider, setIsResizingDivider] = React.useState(false);
  const [activitiesHeight, setActivitiesHeight] = React.useState(200);
  const sidebarRef = React.useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleDividerMouseDown = (e: React.MouseEvent) => {
    setIsResizingDivider(true);
    e.preventDefault();
  };

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = e.clientX;
      if (newWidth >= 200 && newWidth <= 400) {
        onWidthChange(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, onWidthChange]);

  // Handle vertical divider resizing
  React.useEffect(() => {
    const handleDividerMouseMove = (e: MouseEvent) => {
      if (!isResizingDivider || !sidebarRef.current) return;

      const sidebarRect = sidebarRef.current.getBoundingClientRect();
      const sidebarBottom = sidebarRect.bottom;
      const mouseY = e.clientY;

      const newHeight = sidebarBottom - mouseY;

      if (newHeight >= 100 && newHeight <= 400) {
        setActivitiesHeight(newHeight);
      }
    };

    const handleDividerMouseUp = () => {
      setIsResizingDivider(false);
    };

    if (isResizingDivider) {
      document.addEventListener('mousemove', handleDividerMouseMove);
      document.addEventListener('mouseup', handleDividerMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleDividerMouseMove);
      document.removeEventListener('mouseup', handleDividerMouseUp);
    };
  }, [isResizingDivider]);

  return (
    <div
      ref={sidebarRef}
      className="bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 h-screen flex flex-col relative"
      style={{ width: `${width}px` }}
    >
      {/* Logo */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center hover:opacity-80 transition-opacity cursor-pointer w-full"
        >
          <img src="/favicon.svg" alt="Auxilio Travel" className="h-7 w-7" />
          <span className="ml-2 text-2xl font-bold text-cyan-600 dark:text-cyan-400">Auxilio Travel</span>
        </button>
      </div>

      {/* Main Navigation */}
      <div className="overflow-y-auto flex-1" style={{ minHeight: '150px' }}>
        <CollapsibleNav />
      </div>

      {/* Vertical Resize Handle between Navigation and Activities */}
      <div
        className="h-1 cursor-row-resize group hover:bg-cyan-500 transition-colors border-t border-b border-gray-200 dark:border-gray-700 flex-shrink-0 relative"
        onMouseDown={handleDividerMouseDown}
      >
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full"></div>
          <div className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full"></div>
          <div className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full"></div>
        </div>
      </div>

      {/* Activity History */}
      <div className="overflow-y-auto p-4 flex-shrink-0" style={{ height: `${activitiesHeight}px` }}>
        <div>
          <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase mb-2">{t('common:sidebar.activities')}</h3>
          <div className="space-y-1">
            {activities.map((activity) => {
              // Travel Tools activities get cyan border, others get gray
              const travelToolTypes = ['weather_intelligence', 'flight_explorer', 'accommodation_guide', 'ground_transport', 'cultural_hub', 'phrasebook', 'cuisine_intelligence'];
              const isTravelTool = activity.activityType ? travelToolTypes.includes(activity.activityType) : false;
              const colorClass = activity.activityType === 'travel-questions' || isTravelTool
                ? 'border-l-4 border-cyan-500'
                : 'border-l-4 border-gray-400';

              // Route activities to their respective pages
              const activityRouteMap: Record<string, string> = {
                'travel-questions': '/ask-questions',
                'weather_intelligence': '/weather',
                'flight_explorer': '/flights',
                'accommodation_guide': '/accommodations',
                'ground_transport': '/transport',
                'cultural_hub': '/cultural',
                'phrasebook': '/phrasebook',
                'cuisine_intelligence': '/cuisine',
                'itinerary_builder': '/itinerary',
                'travel_glossary': '/travel-glossary',
                'travel_calculator': '/calculators',
              };
              const targetRoute = (activity.activityType && activityRouteMap[activity.activityType]) || '/dashboard';

              return (
                <div
                  key={activity.id}
                  className={`flex items-center justify-between group px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${colorClass}`}
                >
                  <button
                    onClick={() => {
                      setCurrentActivityId(activity.id);
                      navigate(targetRoute, { state: { loadActivityId: activity.id } });
                    }}
                    className="flex-1 text-left text-sm text-gray-700 dark:text-gray-300"
                  >
                    {activity.name}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();

                      const isFirstActivity = activities[0]?.id === activity.id;
                      if (isFirstActivity && (e.shiftKey || e.ctrlKey)) {
                        if (window.confirm(t('common:sidebar.clearAllConfirm', { count: activities.length }))) {
                          clearAllActivities();
                        }
                      } else {
                        if (window.confirm(t('common:sidebar.deleteConfirm', { name: activity.name }))) {
                          removeActivity(activity.id);
                        }
                      }
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all"
                    title={t('common:buttons.delete')}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
            {activities.length === 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 px-4 py-2">{t('common:sidebar.noActivities')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Copyright Notice */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
        <p className="text-xs text-cyan-500 dark:text-cyan-400 text-center font-light">
          Copyright 2026, Auxilio Technologies LLC
        </p>
      </div>

      {/* Resize Handle */}
      <div
        className="absolute top-0 right-0 w-1 h-full cursor-col-resize group hover:bg-cyan-500 transition-colors"
        onMouseDown={handleMouseDown}
      >
        <div className="absolute top-1/2 right-0 -translate-y-1/2 flex flex-col items-center space-y-1 px-0.5 py-2 rounded-l opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full"></div>
          <div className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full"></div>
          <div className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full"></div>
        </div>
      </div>
    </div>
  );
};
